import { localStorageProductionRouteKey } from '@/shared/config/config'
import {
  Button,
  GenericDialog,
  InputGroup,
  useLatinCharacterValidation
} from '@/common'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function CreateProductionRouteDialog({
  isOpen,
  close,
  installationId
}: {
  isOpen: boolean
  close: () => void
  installationId: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [productionRouteName, setProductionRouteName] = useState('')

  const { error: productionRouteNameError } = useLatinCharacterValidation(
    productionRouteName,
    t('productionRoute:creationDialog.input.error'),
    undefined
  )

  const onSubmit = useCallback(
    (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault()
      window.localStorage.removeItem(localStorageProductionRouteKey)
      const productionRoute = {
        installationId: installationId,
        name: productionRouteName,
        aggregatedGood: '',
        assets: [],
        heatFlowSystem: null,
        heatFlowLimestonePurification: null,
        heatFlowSodaAshPurification: null,
        isUsingWasteGas: null,
        electricityProductionMethods: [],
        storedCO2Handling: null,
        productionMethods: []
      }

      window.localStorage.setItem(localStorageProductionRouteKey, JSON.stringify(productionRoute))
      navigate(`/installations/${installationId}/production-routes/new`)
    },
    [installationId, navigate, productionRouteName]
  )

  const onClose = () => {
    close()
  }

  return (
    <GenericDialog
      title={t('productionRoute:creationDialog.title')}
      size="md"
      isOpen={isOpen}
      close={onClose}
    >
      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <InputGroup
            label={t('productionRoute:creationDialog.input.label')}
            placeholder={t('productionRoute:creationDialog.input.placeholder')}
            hint={t('productionRoute:creationDialog.input.hint')}
            value={productionRouteName}
            onChange={evt => setProductionRouteName(evt.target.value)}
            error={productionRouteNameError || undefined}
          />
        </div>
        <div className="gap-2 flex justify-end">
          <Button variant="neutral" type="button" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={productionRouteName.length <= 0 || !!productionRouteNameError}
          >
            {t('common:actions.add')}
          </Button>
        </div>
      </form>
    </GenericDialog>
  )
}
